import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GedService } from 'src/app/core/services/entities/ged.service';
import { GalerieService, PhotoGalerie } from 'src/app/core/services/entities/galerie.service';
import { FermeStateService } from 'src/app/core/services/entities/ferme-state.service';
import { GalerieSelecteurService } from './galerie-selecteur';

/**
 * Photos d'un élément (article de stock, équipement…) : galerie des photos liées, visionneuse plein écran,
 * ajout de fichiers ou d'une photo de la Galerie Wanar (rattachée sans renvoyer le fichier).
 * La plus récente sert de couverture à la carte de l'élément.
 */
@Component({
  selector: 'bay-photos',
  template: `
    <section class="bay-photos" [attr.aria-label]="titre" data-etat="photos">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <h6 class="fw-bold mb-0">{{ titre }} <span *ngIf="photos.length" class="text-muted fw-normal">({{ photos.length }})</span></h6>
        <div *ngIf="!lectureSeule" class="d-flex gap-2">
          <button type="button" class="btn btn-sm fw-semibold" style="background:#f3f4f6;color:#374151;border-radius:10px;"
                  (click)="depuisGalerie()" [disabled]="envoi" data-test="depuis-galerie">
            <span class="material-symbols-outlined align-middle me-1" style="font-size:16px;" aria-hidden="true">photo_library</span>Galerie Wanar
          </button>
          <label class="btn btn-sm fw-semibold mb-0" style="background:#e5eadf;color:#0d631b;border-radius:10px;" [class.disabled]="envoi">
            <span class="material-symbols-outlined align-middle me-1" style="font-size:16px;" aria-hidden="true">add_a_photo</span>
            {{ envoi ? 'Envoi… ' + progression + ' %' : 'Ajouter des photos' }}
            <input type="file" accept="image/*" multiple class="d-none" (change)="ajouter($event)" [disabled]="envoi" data-test="ajouter-photos">
          </label>
        </div>
      </div>
      <p *ngIf="!photos.length && !chargement" class="text-muted mb-0" style="font-size:13px;">Aucune photo pour l'instant.</p>
      <div *ngIf="erreur" class="text-danger small mb-2" role="alert">{{ erreur }}</div>
      <div class="bay-photos-grille">
        <button *ngFor="let p of photos; let i = index" type="button" class="bay-photos-vignette" (click)="ouverte = i"
                [attr.aria-label]="'Voir la photo ' + (p.legende || p.nom || '')" [title]="p.nom || ''">
          <img *ngIf="(src(p) | async) as s" [src]="s" alt="">
        </button>
      </div>
      <bay-visionneuse *ngIf="ouverte != null" [photos]="photos" [index]="ouverte" (indexChange)="ouverte = $event"
                       (fermer)="ouverte = null" (supprimee)="retiree($event)"></bay-visionneuse>
    </section>
  `,
  styles: [`
    .bay-photos-grille { display:grid; grid-template-columns:repeat(auto-fill, minmax(96px, 1fr)); gap:8px; }
    .bay-photos-vignette { aspect-ratio:4/3; border:none; padding:0; border-radius:10px; overflow:hidden; background:#f3f4f6; }
    .bay-photos-vignette img { width:100%; height:100%; object-fit:cover; display:block; }
    .bay-photos-vignette:focus-visible { outline:3px solid #0d631b; outline-offset:2px; }
  `]
})
export class PhotosComponent implements OnChanges {
  @Input() lienType = '';
  @Input() lienId?: number;
  @Input() lienLabel = '';
  @Input() titre = 'Photos';
  @Input() lectureSeule = false;
  /** Fin d'un envoi ou d'un rattachement : la carte de l'élément peut recharger sa couverture */
  @Output() ajoutees = new EventEmitter<void>();

  photos: PhotoGalerie[] = [];
  chargement = false;
  envoi = false;
  progression = 0;
  erreur: string | null = null;
  ouverte: number | null = null;
  private sources = new Map<number, Observable<SafeUrl | null>>();

  constructor(private ged: GedService, private galerie: GalerieService, private fermeState: FermeStateService,
              private sanitizer: DomSanitizer, private selecteur: GalerieSelecteurService) {}

  ngOnChanges(): void { this.charger(); }

  /** Miniature (cache de session), adresse blob: marquée sûre */
  src(p: PhotoGalerie): Observable<SafeUrl | null> {
    let s = this.sources.get(p.id);
    if (!s) {
      s = this.galerie.miniature(p.id).pipe(map(u => u ? this.sanitizer.bypassSecurityTrustUrl(u) : null));
      this.sources.set(p.id, s);
    }
    return s;
  }

  charger(): void {
    if (!this.lienType || !this.lienId) { this.photos = []; return; }
    this.chargement = true;
    this.galerie.page(this.fermeState.fermeId, { lienType: this.lienType, lienId: this.lienId }, null, 200).subscribe({
      next: p => { this.photos = p.items || []; this.chargement = false; },
      error: () => { this.photos = []; this.chargement = false; }
    });
  }

  retiree(id: number): void {
    const i = this.photos.findIndex(p => p.id === id);
    this.photos = this.photos.filter(p => p.id !== id);
    this.ouverte = this.photos.length ? Math.min(Math.max(i, 0), this.photos.length - 1) : null;
    this.ajoutees.emit();
  }

  /** Photo déjà dans la Galerie : rattachée à la fiche sans renvoyer le fichier */
  async depuisGalerie(): Promise<void> {
    const p = await this.selecteur.choisir();
    if (!p || !this.lienId) { return; }
    this.erreur = null;
    this.ged.lier(p.id, { lienType: this.lienType, lienId: this.lienId, lienLabel: this.lienLabel }).subscribe({
      next: () => { this.charger(); this.ajoutees.emit(); },
      error: e => this.erreur = e?.error?.message || 'La photo n\'a pas pu être rattachée.'
    });
  }

  ajouter(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const fichiers = Array.from(input.files || []);
    input.value = '';
    if (!fichiers.length || !this.lienId) { return; }
    this.envoi = true;
    this.erreur = null;
    let restants = fichiers.length;
    const fin = () => { if (--restants === 0) { this.envoi = false; this.progression = 0; this.charger(); this.ajoutees.emit(); } };
    for (const f of fichiers) {
      this.ged.televerser(this.fermeState.fermeId, f, f.name, {
        type: 'PHOTO', dossierCode: 'PHOTOS_FERME', lienType: this.lienType, lienId: this.lienId, lienLabel: this.lienLabel,
      }).subscribe({
        next: p => { this.progression = p.pourcentage; if (p.document) { fin(); } },
        error: e => { this.erreur = e?.error?.message || `« ${f.name} » n'a pas pu être envoyée.`; fin(); }
      });
    }
  }
}
